import type { AxiosResponse } from 'axios';
import { ApiError, type ResponseEnvelope } from '@/types/api';

/**
 * Unwraps a CommandResult / QueryResult envelope.
 *
 * - On `success: true`, returns `data` (asserted non-null — endpoints that
 *   legitimately have no payload should declare `T = void` and the caller
 *   discards the value).
 * - On `success: false`, throws `ApiError` carrying the messages so callers
 *   can render server-provided copy directly without parsing.
 */
export function unwrap<T>(response: AxiosResponse<ResponseEnvelope<T>>): T {
  const { data, status } = response;

  if (!data || typeof data !== 'object') {
    throw new ApiError(status, [
      { code: 'MALFORMED_RESPONSE', message: 'Malformed server response', type: 'ERROR' },
    ]);
  }

  if (data.success) {
    return data.data as T;
  }

  throw new ApiError(status, data.messages ?? []);
}

/** Type guard for narrowing thrown values in catch blocks. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}
